import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  PLACE_NEW_COURSE_LOOKUP_FOUND_COURSE,
  PlaceNewCourseLookupFoundCourseVariables,
  PlaceNewCourseLookupFoundCourseResponse
} from 'share/mutations/courses'
import { useSelector } from 'react-redux'
import {
  setCasePlaceOrderUpdateFields,
  isDebtorType1
} from 'reducers/activecase/activecaseSlice'
import { setTitle } from 'reducers/activecourse/activecourseSlice'
import {
  CourseTabTitle,
  getCourseStatusName,
  CourseOrderItem,
  CourseType
} from 'share/domain/course'
import Button from 'react-bootstrap/Button'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'

interface Props {
  course: CourseOrderItem | null
}

const CoursePlaceOrderLookupFoundCourse = (props: Props): JSX.Element => {
  const isDebtor1 = useSelector(isDebtorType1)
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const [courseName, setCourseName] = useState('first')
  const [submitError, setSubmitError] = useState('')

  const [acceptedFoundCourse, { loading }] = useMutation<
    PlaceNewCourseLookupFoundCourseResponse,
    PlaceNewCourseLookupFoundCourseVariables
  >(PLACE_NEW_COURSE_LOOKUP_FOUND_COURSE)

  useEffect(() => {
    let title = ''
    if (activeCourse?.activeTab) {
      title =
        activeCourse.activeTab === 'first'
          ? CourseTabTitle.TAKE_FIRST_COURSE
          : CourseTabTitle.TAKE_SECOND_COURSE
    }

    if (activeCourse?.activeTab === 'second') {
      setCourseName('second')
    }

    dispatch(setTitle(title))
  }, [activeCourse?.activeTab])

  const loadingDelay = useLoadingDelay(loading)

  async function acceptLookupFoundCourseHelper(): Promise<boolean> {
    let result = false

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType:
          activeCourse?.activeTab === 'first'
            ? CourseType.CreditCounseling
            : CourseType.DebtorEducation,
        isDebtor1: isDebtor1,
        orderItemSourceTypeId: activeCase?.matter?.orderItemSourceTypeId ?? 0,
        isJoint: activeCase?.matter?.isJoint ?? false
      }

      const acceptedFoundCourseResult = await acceptedFoundCourse({
        variables: { request: input }
      })

      if (
        !acceptedFoundCourseResult.data?.orders.placeNewCourseLookupFoundCourse
          .success
      ) {
        setSubmitError('Error occurred while accepting lookup found course')
        result = false
      } else {
        result = true
        dispatch(
          setCasePlaceOrderUpdateFields(
            acceptedFoundCourseResult.data?.orders
              .placeNewCourseLookupFoundCourse
          )
        )
      }
    } catch (e) {
      setSubmitError('Error occurred while creating order account')
      result = false
    }

    return result
  }

  const onClickNext = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.preventDefault()
    acceptLookupFoundCourseHelper()
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}

      <div>
        <h2>Existing Course Order Found</h2>
        <h4>
          An existing {courseName} course was found with the following status{' '}
          {props.course && getCourseStatusName(props.course.courseStatus, true)}
        </h4>
        <div className='row'>
          <div className='col-sm-12 offset-md-3 offset-lg-2 col-md-6 col-lg-8 btn-container'>
            <Button
              data-testid='create-account-bookmark-goto-dashboard-button'
              type='button'
              className='btn btn-submit w-100 mt-3'
              onClick={onClickNext}
            >
              Next
            </Button>
          </div>
        </div>
        {submitError && <div>{submitError}</div>}
      </div>
    </>
  )
}

export default CoursePlaceOrderLookupFoundCourse
